import { connect } from "react-redux";
import React from "react";
import {
  Button,
  Container,
  Badge,
  Modal,
  Form,
  Spinner,
  Alert,
} from "react-bootstrap";

import AppComponent from "../../../Components/AppComponent";
import SearchBox from "../../../Components/Input/SearchBox";
import ItemList from "../../Components/ItemList";
import UnpActions from "../../../Stores/redux/UnPersist/Actions";
import PActions from "../../../Stores/redux/Persist/Actions";
import api from "../../../Services/Api/api";
import EditAgent from "./EditAgent";
import NavBar from "../../Components/NavBar";

const SCREEN_NAME = "AGENTS_SCREEN";

// Confirm token the user must type to actually trigger calculation
const COMMISSION_CONFIRM_TOKEN = "CALCULATE";

class AgentsScreen extends AppComponent {
  state = {
    visibleModal: null,
    modalData: null,
    triggerReload: 0,
    error: null,

    // for manual calculation confirmation modal
    calcModalVisible: false,
    calcModalAgent: null,
    calcConfirmInput: "",
    calcLoading: false,
    calcError: null,
    calcSuccessMessage: null,
  };

  additionalQueries = null;

  columns = [
    {
      value: "name",
      label: "Name",
      render: ({ item }) =>
        [item.firstName, item.lastName].filter(Boolean).join(" ") || "—",
    },
    {
      value: "phone",
      label: "Phone",
      render: ({ item }) => item.phone || "—",
    },
    {
      value: "email",
      label: "Email",
      render: ({ item }) => item.email || "—",
    },
    {
      value: "pickupRate",
      label: "Pickup Rate",
      render: ({ item }) =>
        item.agent?.rates?.pickup ? `₹${item.agent.rates.pickup}` : "—",
    },
    {
      value: "deliveryRate",
      label: "Delivery Rate",
      render: ({ item }) =>
        item.agent?.rates?.delivery ? `₹${item.agent.rates.delivery}` : "—",
    },
    {
      value: "workingArea",
      label: "Working Area",
      render: ({ item }) => item.agent?.workingArea || "—",
    },
    {
      value: "isActive",
      label: "Agent Active",
      render: ({ item }) =>
        item.agent?.isActive ? (
          <Badge bg="success">Yes</Badge>
        ) : (
          <Badge bg="secondary">No</Badge>
        ),
    },
    {
      value: "actions",
      label: "Actions",
      render: ({ item }) => (
        <div
          style={{
            textAlign: "center",
            display: "flex",
            gap: 8,
            justifyContent: "center",
          }}
        >
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() =>
              this.setAsyncState({
                visibleModal: "editAgent",
                modalData: { agent: item },
              })
            }
          >
            Edit
          </Button>

          <Button
            size="sm"
            variant="outline-danger"
            onClick={() =>
              this.setAsyncState({
                calcModalVisible: true,
                calcModalAgent: item,
                calcConfirmInput: "",
                calcError: null,
                calcSuccessMessage: null,
              })
            }
          >
            Comm Settle
          </Button>
        </div>
      ),
    },
  ];

  async retriveData(query) {
    const queryStr = JSON.stringify({
      where: { role: "agent", ...(this.additionalQueries?.where || {}) },
    });

    const res = await api.get("v1/office/user", { queryStr });
    return res?.users || [];
  }

  async setItems(items) {
    this.props.setScreenState({ agents: items });
  }

  handleSearchQ(q) {
    if (q) {
      this.additionalQueries = {
        where: {
          $or: [
            { username: { $regex: q, $options: "i" } },
            { firstName: { $regex: q, $options: "i" } },
            { lastName: { $regex: q, $options: "i" } },
            { phone: { $regex: q, $options: "i" } },
            { email: { $regex: q, $options: "i" } },
          ],
        },
      };
    } else {
      this.additionalQueries = null;
    }
    this.setAsyncState({ triggerReload: Date.now() });
  }

  onNewAgent() {
    this.setAsyncState({
      visibleModal: null,
      modalData: null,
      triggerReload: Date.now(),
    });
  }

  // Called when user confirms the calculation modal
  async handleConfirmCalculate() {
    const { calcModalAgent, calcConfirmInput } = this.state;

    // simple guard
    if (!calcModalAgent) return;

    // require typing exact token to proceed
    if ((calcConfirmInput || "").trim() !== COMMISSION_CONFIRM_TOKEN) {
      this.setAsyncState({
        calcError: `Type "${COMMISSION_CONFIRM_TOKEN}" to confirm.`,
      });
      return;
    }

    this.setAsyncState({
      calcLoading: true,
      calcError: null,
      calcSuccessMessage: null,
    });

    try {
      // call backend - adjust endpoint/payload if your API differs
      const res = await api.put(
        `v1/office/user/${calcModalAgent._id}/settle-commission`
      );

      const msg =
        res.message ||
        `Commission calculated successfully for ${
          [calcModalAgent.firstName, calcModalAgent.lastName]
            .filter(Boolean)
            .join(" ") ||
          calcModalAgent.username ||
          calcModalAgent._id
        }.`;

      this.setAsyncState({
        calcSuccessMessage: msg,
        calcLoading: false,
      });
    } catch (err) {
      console.error("Commission calculation failed", err);
      this.setAsyncState({
        calcError: err?.message || "Failed to calculate commission.",
        calcLoading: false,
      });
    }
  }

  render() {
    const {
      state: {
        visibleModal,
        modalData,
        triggerReload,
        calcModalVisible,
        calcModalAgent,
        calcConfirmInput,
        calcLoading,
        calcError,
        calcSuccessMessage,
      },
      props: { agents },
    } = this;

    const screen = (
      <div>
        <div className="pagetitlebar d-flex justify-content-between align-items-center mb-3">
          <div className="title h5 mb-0">Field Executives</div>
          <div className="d-flex gap-2">
            <SearchBox onChange={this.handleSearchQ.bind(this)} />
            <Button
              onClick={() =>
                this.setAsyncState({
                  visibleModal: "editAgent",
                  modalData: null,
                })
              }
            >
              + Add Agent
            </Button>
          </div>
        </div>

        <ItemList
          {...{
            disablePagination: true,
            defaultQuery: {},
            triggerReload,
            items: agents,
            table: {
              columns: this.columns,
            },
            retriveData: this.retriveData.bind(this),
            setItems: this.setItems.bind(this),
            additionalQueries: this.additionalQueries,
          }}
        />

        <EditAgent
          key={`editAgent-${
            visibleModal === "editAgent"
              ? modalData?.agent?._id?.toString() || "new"
              : "disabled"
          }`}
          renderModal
          visible={visibleModal === "editAgent"}
          handleClose={() => this.setAsyncState({ visibleModal: null })}
          onSubmit={() => this.onNewAgent()}
          {...(modalData || {})}
        />

        {/* Confirmation modal for commission calculation */}
        <Modal
          show={!!calcModalVisible}
          onHide={() =>
            this.setAsyncState({
              calcModalVisible: false,
              calcModalAgent: null,
              calcConfirmInput: "",
              calcError: null,
              calcSuccessMessage: null,
              calcLoading: false,
            })
          }
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Manual Commission Calculation</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>
              <p>
                You are about to manually trigger commission settlement for{" "}
                <strong>
                  {calcModalAgent
                    ? [calcModalAgent.firstName, calcModalAgent.lastName]
                        .filter(Boolean)
                        .join(" ")
                    : "the agent"}
                </strong>
                .
              </p>
              <p className="text-muted">
                This action will run the same settlement logic that normally
                runs automatically. This button is intended for operator use
                only.
              </p>

              {calcSuccessMessage ? (
                <Alert variant="success">{calcSuccessMessage}</Alert>
              ) : null}

              {calcError ? <Alert variant="danger">{calcError}</Alert> : null}

              <Form.Group className="mb-3" controlId="calcConfirm">
                <Form.Label>
                  Type <code>{COMMISSION_CONFIRM_TOKEN}</code> to confirm
                </Form.Label>
                <Form.Control
                  value={calcConfirmInput}
                  onChange={(e) =>
                    this.setAsyncState({
                      calcConfirmInput: e.target.value,
                      calcError: null,
                    })
                  }
                  placeholder={`Type ${COMMISSION_CONFIRM_TOKEN} to confirm`}
                  disabled={calcLoading}
                />
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() =>
                this.setAsyncState({
                  calcModalVisible: false,
                  calcModalAgent: null,
                  calcConfirmInput: "",
                  calcError: null,
                  calcSuccessMessage: null,
                })
              }
              disabled={calcLoading}
            >
              Close
            </Button>
            <Button
              variant="danger"
              onClick={() => this.handleConfirmCalculate()}
              disabled={calcLoading}
            >
              {calcLoading ? (
                <>
                  <Spinner
                    animation="border"
                    size="sm"
                    role="status"
                    className="me-2"
                  />
                  Calculating...
                </>
              ) : (
                "Run Calculation"
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );

    return (
      <>
        <NavBar activeLink="/fe" />
        <Container className="gainner">{screen}</Container>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  agents: state.vState[SCREEN_NAME]?.agents || [],
  // optionally provide current user info so you can gate visibility by role
  currentUser: state.auth?.user || null,
});

const mapDispatchToProps = (dispatch) => ({
  setScreenState: (obj, persist = false, screenName = SCREEN_NAME) =>
    persist
      ? dispatch(PActions.setPScreenState(screenName, obj))
      : dispatch(UnpActions.setVScreenState(screenName, obj)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AgentsScreen);
